import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Eye, Download, FileText, Loader2, Printer, Image as ImageIcon, Share2 } from 'lucide-react';
import { useDivision7Filters } from '../shared/useDivision7Filters';
import { Division7FilterBar } from '../shared/Division7FilterBar';
import { useDownload, formatBytes } from '../../context/DownloadContext';
import { SubmittalPreviewModal } from '../../components/SubmittalPreviewModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import AddJointProtectionWizard from './AddJointProtectionWizard';
import { JointProtectionPrintView, JointProtectionQR, JointProtectionPrintViewInline } from './JointProtectionPrintView';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ShareSection } from '../shared/ShareSection';
import { generatePdfFromElement, generatePdfFromModalContent, buildPdfFilename } from '../../utils/pdfExport';
import { QRThumbnail } from '../shared/PrintComponents';
import { OrangeCheckbox, QRPreviewModal } from '../shared/WizardComponents';
import { getCountryName } from '../../utils/countries';

export const JointProtectionSection = ({
  canEditCSIItems, canManageProject, companyId, projectId, csiItem, project, user,
  shareCompanySearch, showCompanySuggestions, companySuggestions, highlightedIndex, searchingCompanies,
  handleSearchCompanies, handleCompanySearchKeyDown, setShowCompanySuggestions, setHighlightedIndex,
  handleSelectCompany, handleUnshareCSI,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { filtered, filters, setFilter, clearFilters, filterOptions, schema, activeCount } = useDivision7Filters('joint-protection', products);
  const [editProduct, setEditProduct] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [qrModal, setQrModal] = useState({ open: false, qrToken: null, productName: '' });
  const [deleting, setDeleting] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [previewSubmittal, setPreviewSubmittal] = useState(null);
  const [printProduct, setPrintProduct] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const pdfRef = React.useRef(null);
  const { startDownload, getState: getDlState } = useDownload();

  const csiItemId = csiItem?.id;

  const fetchProducts = useCallback(async () => {
    try {
      const url = csiItemId ? `${API}/joint-protection-products/${projectId}?csi_item_id=${csiItemId}` : `${API}/joint-protection-products/${projectId}`;
      const res = await axios.get(url);
      setProducts(res.data || []);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, [projectId, csiItemId]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (id) => {
    setDeleting(id);
    try { await axios.delete(`${API}/joint-protection-products/${projectId}/${id}`); toast.success('Product deleted'); fetchProducts(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleteConfirm(null); setDeleting(null); }
  };

  const handlePreviewProduct = async (product) => {
    try {
      const res = await axios.get(`${API}/joint-protection-products/${projectId}/${product.id}`);
      setPrintProduct(res.data);
    } catch { toast.error('Failed to load product details'); }
  };

  const handleListDownloadPdf = async (product) => {
    setPdfLoading(product.id);
    try {
      const res = await axios.get(`${API}/joint-protection-products/${projectId}/${product.id}`);
      const pdfProduct = res.data;
      if (!pdfProduct || !pdfRef.current) return;
      await new Promise(r => setTimeout(r, 100));
      pdfRef.current.innerHTML = '';
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(pdfRef.current);
      root.render(React.createElement(JointProtectionPrintViewInline, { product: pdfProduct, project }));
      await new Promise(r => setTimeout(r, 500));
      await generatePdfFromElement(pdfRef.current, buildPdfFilename(pdfProduct));
      root.unmount();
    } catch { toast.error('Failed to generate PDF'); }
    finally { setPdfLoading(null); }
  };

  const handleModalDownloadPdf = async () => {
    try { await generatePdfFromModalContent('jp-print-content-area', buildPdfFilename(printProduct)); }
    catch { toast.error('Failed to generate PDF'); }
  };

  const handleBulkDelete = async () => {
    try { await axios.post(`${API}/joint-protection-products/${projectId}/bulk-delete`, { ids: [...selectedIds] }); toast.success(`${selectedIds.size} products deleted`); setSelectedIds(new Set()); fetchProducts(); }
    catch { toast.error('Bulk delete failed'); }
    finally { setBulkDeleteConfirm(false); }
  };

  const handlePreviewSubmittal = async (product, submittal) => {
    try {
      const res = await axios.get(`${API}/joint-protection-products/${projectId}/${product.id}/submittals/${submittal.id}/preview-url`);
      setPreviewSubmittal({ ...submittal, cloudinaryUrl: res.data?.url });
    } catch { toast.error('Failed to load preview'); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => { if (selectedIds.size === filtered.length) setSelectedIds(new Set()); else setSelectedIds(new Set(filtered.map(p => p.id))); };

  if (loading) return <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading...</div>;

  return (
    <div className="space-y-4" data-testid="jp-section">
      {/* Filter bar */}
      <Division7FilterBar filters={filters} setFilter={setFilter} clearFilters={clearFilters} filterOptions={filterOptions} schema={schema} activeCount={activeCount} testIdPrefix="jp" />
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-4">
          {canManageProject && (
            <div className="text-xs text-slate-600" data-testid="jp-share-info">
              Shared with: {csiItem?.assignedCompanyName ? (
                <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
              ) : (
                <span className="text-slate-400">N/A</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canManageProject && (
            <Button variant="destructive" size="sm" onClick={() => setBulkDeleteConfirm(true)} disabled={selectedIds.size === 0} data-testid="jp-bulk-delete-btn">
              <Trash2 className="h-4 w-4 mr-1.5" />Delete All{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          )}
          {canEditCSIItems && (
            <Button size="sm" onClick={() => { setEditProduct(null); setWizardOpen(true); }} data-testid="jp-create-product-btn">
              <Plus className="h-4 w-4 mr-1.5" />Create Product
            </Button>
          )}
          {canManageProject && csiItem && (
            <Button variant="outline" size="sm" onClick={() => setShowShareModal(true)} data-testid="jp-share-btn">
              <Share2 className="h-4 w-4 mr-1.5" />Share
            </Button>
          )}
        </div>
      </div>

      {/* Product List */}
      {filtered.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-8 text-center" data-testid="jp-empty-state">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-slate-700 mb-1">{products.length === 0 ? 'No Joint Protection Products' : 'No products match the current filters'}</h3>
          {products.length === 0 && <p className="text-xs text-slate-400 mb-4">Add your first joint protection product to get started.</p>}
          {products.length === 0 && canEditCSIItems && <Button size="sm" onClick={() => { setEditProduct(null); setWizardOpen(true); }}><Plus className="h-4 w-4 mr-1.5" />Create Product</Button>}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="jp-products-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="w-10 px-3 py-3">
                  <OrangeCheckbox id="jp-select-all" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} testId="jp-select-all-checkbox" />
                </th>
                <th className="w-12 px-3 py-3 text-left font-semibold text-slate-700">Image</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Product Name / Model</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Country of Manufacturer</th>
                <th className="w-12 px-3 py-3 text-center font-semibold text-slate-700">QR</th>
                <th className="w-20 px-3 py-3 text-left font-semibold text-slate-700">Status</th>
                <th className="w-32 px-3 py-3 text-center font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(p => {
                const isSelected = selectedIds.has(p.id);
                return (
                  <tr key={p.id} className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-orange-50/50' : ''}`} data-testid={`jp-product-row-${p.id}`}>
                    <td className="w-10 px-3 py-2">
                      <OrangeCheckbox id={`jp-select-row-${p.id}`} checked={isSelected} onChange={() => toggleSelect(p.id)} testId={`jp-select-row-${p.id}`} />
                    </td>
                    <td className="w-12 px-3 py-2">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt="" className="w-10 h-10 rounded object-cover border border-slate-200" data-testid={`jp-product-image-${p.id}`} />
                      ) : (
                        <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center"><ImageIcon className="h-4 w-4 text-slate-300" /></div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{p.manufacturerName || '—'}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{p.productNameModel || 'Untitled'}</td>
                    <td className="px-3 py-2 text-slate-600">{p.countryCode ? getCountryName(p.countryCode) : '—'}</td>
                    <td className="w-12 px-3 py-2 text-center" data-testid={`jp-qr-cell-${p.id}`}>
                      <button type="button" className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                        onClick={() => setQrModal({ open: true, qrToken: p.qrToken, productName: p.productNameModel })}
                        data-testid={`jp-qr-thumbnail-${p.id}`} title="Click to enlarge QR code"
                      >
                        <QRThumbnail qrToken={p.qrToken} size={32} />
                      </button>
                    </td>
                    <td className="w-20 px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${p.status === 'active' ? 'bg-green-600 text-white' : 'bg-amber-100 text-amber-700'}`} data-testid={`jp-product-status-${p.id}`}>
                        {p.status || 'draft'}
                      </span>
                    </td>
                    <td className="w-32 px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 transition-colors ${p.status === 'active' ? 'text-slate-400 hover:bg-orange-500 hover:text-white' : 'text-slate-300 cursor-not-allowed'}`} onClick={() => handlePreviewProduct(p)} disabled={p.status !== 'active'} title={p.status === 'active' ? 'Preview' : 'Only active products can be previewed'} data-testid={`print-product-${p.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 transition-colors ${p.status === 'active' ? 'text-slate-400 hover:bg-orange-500 hover:text-white' : 'text-slate-300 cursor-not-allowed'}`} onClick={() => handleListDownloadPdf(p)} disabled={p.status !== 'active' || pdfLoading === p.id} title="Download PDF" data-testid={`download-pdf-${p.id}`}>
                          {pdfLoading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        </Button>
                        {canEditCSIItems && (
                          <>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => { setEditProduct(p); setWizardOpen(true); }} title="Edit" data-testid={`edit-product-${p.id}`}><Edit2 className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => setDeleteConfirm(p.id)} title="Delete" data-testid={`delete-product-${p.id}`}>
                              {deleting === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Wizard */}
      <AddJointProtectionWizard
        open={wizardOpen} onOpenChange={setWizardOpen}
        projectId={projectId} companyId={companyId} csiItemId={csiItemId}
        editProduct={editProduct} onRefresh={fetchProducts}
      />

      {/* Modals */}
      <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />
      <ConfirmDialog open={!!deleteConfirm} title="Delete Product" description="Are you sure you want to delete this product?" confirmLabel="Delete" cancelLabel="Cancel" variant="destructive" onConfirm={() => handleDelete(deleteConfirm)} onClose={() => setDeleteConfirm(null)} />
      <ConfirmDialog open={bulkDeleteConfirm} title="Delete Products" description={`Are you sure you want to delete ${selectedIds.size} products?`} confirmLabel="Delete All" cancelLabel="Cancel" variant="destructive" onConfirm={handleBulkDelete} onClose={() => setBulkDeleteConfirm(false)} />
      <QRPreviewModal open={qrModal.open} onClose={() => setQrModal({ open: false, qrToken: null, productName: '' })} qrToken={qrModal.qrToken} productName={qrModal.productName} />

      {/* Share Modal */}
      {csiItem && (
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="max-w-2xl" data-testid="jp-share-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />Share This Specification
              </DialogTitle>
              <DialogDescription>
                Share {csiItem.itemTitle} specification with a sub-contractor company
              </DialogDescription>
            </DialogHeader>
            <ShareSection
              asModal
              tabValue=""
              inputIdPrefix="jp"
              csiItem={csiItem}
              project={project}
              user={user}
              shareCompanySearch={shareCompanySearch}
              showCompanySuggestions={showCompanySuggestions}
              companySuggestions={companySuggestions}
              highlightedIndex={highlightedIndex}
              searchingCompanies={searchingCompanies}
              handleSearchCompanies={handleSearchCompanies}
              handleCompanySearchKeyDown={handleCompanySearchKeyDown}
              setShowCompanySuggestions={setShowCompanySuggestions}
              setHighlightedIndex={setHighlightedIndex}
              handleSelectCompany={handleSelectCompany}
              handleUnshareCSI={handleUnshareCSI}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Print Preview Modal — TI pattern */}
      <Dialog open={!!printProduct} onOpenChange={(v) => { if (!v) setPrintProduct(null); }}>
        <DialogContent className="max-w-3xl p-0 flex flex-col" style={{ height: '85vh', maxHeight: '85vh' }} data-testid="jp-print-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5" />Preview</DialogTitle>
            <DialogDescription>Review before printing or downloading</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto border rounded-lg mx-4 my-2 bg-white">
            {printProduct && <JointProtectionPrintView product={{ ...printProduct, manufacturerName: printProduct.manufacturerName }} project={project} />}
          </div>
          <div className="flex justify-end gap-2 px-6 py-3 border-t shrink-0">
            <Button variant="outline" onClick={() => setPrintProduct(null)} data-testid="jp-print-close-btn">Close</Button>
            <Button variant="outline" onClick={() => handleModalDownloadPdf()} data-testid="jp-print-download-btn"><Download className="h-4 w-4 mr-1.5" />Download</Button>
            <Button onClick={() => { window.print(); }} data-testid="jp-print-print-btn"><Printer className="h-4 w-4 mr-1.5" />Print</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden PDF render container */}
      <div ref={pdfRef} style={{ position: 'fixed', left: '-10000px', top: 0 }} />
    </div>
  );
};
